import axios, { AxiosError, AxiosResponse, Method } from 'axios'
import jwtDecode from 'jwt-decode'

export const LOCAL_ORGANIZATION_KEY = 'localOrganization'

interface IToken {
  current: string | false
  expire: number
}

const apiToken: IToken = {
  current: false,
  expire: 0,
}

export const getWebsocketUrl = () => `${process.env.WEBSOCKET_API_URL || ''}?idToken=${apiToken.current || ''}`

export const setApiToken = (token: string) => {
  if (!token) return
  try {
    const data: { exp: number } = jwtDecode(token)
    apiToken.expire = data.exp * 1000
    apiToken.current = token
  } catch (err) {
    console.log(err)
  }
}

interface UpdateTokenHeaders {
  'update-token'?: string
}

export interface ApiError extends AxiosError {
  _apierror?: boolean
}

export async function request<Response, Body = unknown>(
  method: Method,
  url: string,
  data?: Body,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  logoutOn403 = true,
  signal?: AbortSignal
): Promise<AxiosResponse<Response>> {
  const headers: { [name: string]: string } = {}

  if (apiToken.current) headers.Authorization = `Bearer ${apiToken.current}`

  // Add the organization that is shown in the browser as a header
  // to make sure that the request is handled according to the correct
  // organization.
  const localOrgKey = sessionStorage.getItem(LOCAL_ORGANIZATION_KEY)
  if (localOrgKey) headers['X-User-Organization'] = localOrgKey

  const params = {
    method,
    url,
    headers,
    data: data || {},
    signal,
  }

  try {
    const res = await axios(params)
    const headers = res.headers as UpdateTokenHeaders
    if (headers['update-token']) setApiToken(headers['update-token'])
    return res as AxiosResponse<Response>
  } catch (caught) {
    // A catch binding is `unknown`; everything axios rejects with is an AxiosError.
    const err = caught as ApiError
    const requestObj = err.request as XMLHttpRequest | undefined
    const response = requestObj?.response as string | undefined

    // Aborted through the caller's signal. Rethrow so the caller sees axios' CanceledError: an
    // aborted XHR has `response === ''`, and returning that as a response made callers read
    // `data` off an empty string (DEV-3341).
    if (err.code === 'ERR_CANCELED') throw err

    const isAccessDeniedError = (response || '').indexOf('access_denied') > -1
    if (isAccessDeniedError && logoutOn403 && requestObj && requestObj.status === 403) {
      window.location.href = '/logout'
      return requestObj.response as AxiosResponse<Response>
    }

    console.log(err)

    err._apierror = true
    throw err
  }
}
